import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, LogOut, ArrowLeft, User } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cinco-admin/artifacts/gnzt6mea_knto-cinco-icon-black-rgb-900px-w-72ppi.jpg";

const SECTION_LABELS = {
  inventory: "Inventory",
  printables: "Printables",
  documents: "Documents",
  "employee-docs": "Employee Docs",
  bills: "Bills",
  payroll: "Payroll",
  sales: "Sales",
};

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Path structure: /dashboard, /dashboard/:section, /dashboard/:section/:branch, /dashboard/:section/:branch/:subPage
  const pathParts = location.pathname.split("/").filter(Boolean);
  const sectionKey = pathParts[1] || "";

  const handleBack = () => {
    // Go back one level by removing the last path segment
    const parentPath = "/" + pathParts.slice(0, -1).join("/");
    navigate(parentPath);
  };

  // Dynamic back label based on depth
  const getBackLabel = () => {
    if (pathParts.length <= 1) return null;
    if (pathParts.length === 2) return "Dashboard";
    if (pathParts.length === 3) return SECTION_LABELS[sectionKey] || sectionKey;
    // depth 4+: show branch name or parent label
    const branch = pathParts[2];
    if (branch === "puan") return "Puan";
    if (branch === "lapu-lapu") return "Lapu-Lapu";
    return SECTION_LABELS[sectionKey] || "Back";
  };
  const backLabel = getBackLabel();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const roleLabel = {
    admin: "Admin",
    branch_manager: "Branch Manager",
    staff: "Staff",
    master_admin: "Master Admin",
    lapu_lapu_staff: "Lapu-Lapu Staff",
    puan_staff: "Puan Staff",
  };

  return (
    <header
      className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-6 sticky top-0 z-30"
      data-testid="dashboard-header"
    >
      {/* Left */}
      <div className="flex items-center gap-4">
        {backLabel && (
          <>
            <Button
              variant="ghost"
              size="sm"
              data-testid="back-btn"
              onClick={handleBack}
              className="text-zinc-500 hover:text-zinc-900 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {backLabel}
            </Button>
            <div className="h-5 w-px bg-zinc-200" />
          </>
        )}
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="" className="w-6 h-6" />
          <span className="font-heading font-semibold text-zinc-900">Knto Cinco</span>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              data-testid="user-menu-trigger"
              className="flex items-center gap-2 hover:bg-zinc-50"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <User className="w-5 h-5 text-zinc-500" />
              )}
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-zinc-900 leading-none">{user?.name}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{user?.role_display || roleLabel[user?.role] || "Staff"}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-zinc-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-zinc-500">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-red-600 cursor-pointer"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
